import { RatingStars } from '@/components/RatingStars';
import SvgIcon from '@/components/SvgIcon';
import { CenterReviewSummary } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { MediumText, RegularText } from '@/lib/Text';
import { Col } from '@/lib/utils';
import React from 'react';

type Props = {
    centerReviewSummary?: CenterReviewSummary;
};

function FeedInstructorReviewsCenterSummary({ centerReviewSummary }: Props) {
    const centerName = centerReviewSummary?.centerName || '';
    const centerAverageRating = centerReviewSummary?.centerAverageRating ?? 0;
    const averageChange = centerReviewSummary?.centerAverageRatingChange ?? 0;
    const isIncrease = averageChange >= 0;
    const changeValue = Math.abs(averageChange).toFixed(1);
    const memberCount = centerReviewSummary?.centerReviewMemberCount ?? 0;
    const answerCount = centerReviewSummary?.centerAnswerCount ?? 0;
    const firstRating = centerReviewSummary?.centerFirstClassAverageRating ?? 0;
    const middleRating = centerReviewSummary?.centerMiddleClassAverageRating ?? 0;
    const lastRating = centerReviewSummary?.centerLastClassAverageRating ?? 0;
    const positiveRate = centerReviewSummary?.centerPositiveAnswerRate ?? 0;
    const negativeRate = centerReviewSummary?.centerNegativeAnswerRate ?? 0;
    const stageRatings = [
        { label: '첫', value: firstRating },
        { label: '중간', value: middleRating },
        { label: '마지막', value: lastRating },
    ];

    return (
        <div className="flex flex-col bg-[#FFFFFF] border-[0.5px] border-[#E8E8E8] w-[474px] px-[36px] pt-[36px] pb-[40px] rounded-[25px] ">
            <div className="flex flex-row items-center mb-[20px]">
                <div className="bg-[#E7EAF0] w-[44px] h-[44px] rounded-full flex justify-center items-center mr-[8px]">
                    <SvgIcon name="SvgHome" size={20} fill={colors.trans} />
                </div>
                <MediumText fontSize={20} lineHeight={30} color={colors.Text_1} letterSpacing={-0.3}>
                    {centerName}
                </MediumText>
            </div>
            <div className="mb-[29px] flex flex-row">
                {/* 별점 */}
                <RatingStars rating={centerAverageRating} size={24} gap={0} className="mr-[31px]" />
                {/*  */}
                <div className="flex flex-col gap-[11px]">
                    <div className="flex items-center">
                        <RegularText
                            fontSize={16}
                            lineHeight={21}
                            color={colors.Text_1}
                            className="mr-[10px]"
                            letterSpacing={-0.7}
                        >
                            1개월 전보다
                        </RegularText>
                        <SvgIcon
                            name={'SvgSortArrow'}
                            size={12}
                            fill={isIncrease ? colors.GREEN : colors.ERROR}
                            className={`mr-[4px] ${isIncrease ? '' : 'transform rotate-180'}`}
                        />
                        <RegularText fontSize={16} lineHeight={21} color={isIncrease ? colors.GREEN : colors.ERROR}>
                            {changeValue}
                        </RegularText>
                    </div>
                    <div className="flex items-center">
                        <SvgIcon
                            name="SvgCheck"
                            size={14}
                            stroke={colors.Text_1}
                            strokeWidth={1.75}
                            className="mr-[8px]"
                            fill={colors.trans}
                        />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.4}>
                            {memberCount.toLocaleString('ko-KR')}명 참여
                        </RegularText>
                    </div>
                    <div className="flex items-center">
                        <SvgIcon
                            name="SvgLineHistory"
                            size={14}
                            fill={colors.white}
                            className="mr-[8px]"
                            strokeWidth={1.25}
                        />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.4}>
                            {answerCount.toLocaleString('ko-KR')}회 의견
                        </RegularText>
                    </div>
                </div>
            </div>
            <Col className="w-full h-[1px] bg-[#E5E5E5] rounded-full mb-[25px]" />

            <div className="flex flex-col gap-[16px] mb-[40px]">
                {stageRatings.map((stage, index) => {
                    const progress = Math.min(Math.max((stage.value / 5) * 100, 0), 100);
                    return (
                        <div key={stage.label} className="flex items-center">
                            <div className="flex items-center w-[80px] mr-[49px]">
                                <div className="flex items-center justify-center bg-[#646869] w-[16px] h-[16px] rounded-[5px] mr-[16px]">
                                    <MediumText fontSize={10} lineHeight={18} color={colors.white}>
                                        {index + 1}
                                    </MediumText>
                                </div>
                                <RegularText fontSize={17} lineHeight={24} color={'#192B28'}>
                                    {stage.label}
                                </RegularText>
                            </div>
                            <div className="flex-1 flex items-center">
                                <div className="relative w-full h-[7px] bg-[#F2F4F6] rounded-full overflow-hidden mr-[33px]">
                                    <div
                                        className="absolute left-0 top-0 h-full bg-[#5E7093] rounded-full"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <div className="flex items-center gap-[8px] ">
                                    <svg
                                        className="w-[9px] h-[9px] text-[#3D73DD]"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                    <MediumText fontSize={17} lineHeight={22} color={'#192B28'} letterSpacing={-0.7}>
                                        {stage.value.toFixed(1)}
                                    </MediumText>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="relative w-full h-[6px] bg-[#F1F2F4] rounded-full overflow-hidden mb-[14px]">
                <div
                    className="absolute left-0 top-0 h-full bg-[#59D696] "
                    style={{ width: `${Math.min(Math.max(positiveRate, 0), 100)}%` }}
                />
                <div
                    className="absolute right-0 top-0 h-full bg-[#FF999B] "
                    style={{ width: `${Math.min(Math.max(negativeRate, 0), 100)}%` }}
                />
            </div>
            <div className=" flex items-center justify-between">
                <div className="flex items-center gap-[6px]">
                    <RegularText fontSize={15} lineHeight={21} color={colors.Text_1}>
                        좋았어요
                    </RegularText>
                    <MediumText fontSize={15} lineHeight={21} color={'#25B46D'}>
                        {Math.round(positiveRate)}%
                    </MediumText>
                </div>
                <div className="flex items-center gap-[6px]">
                    <RegularText fontSize={15} lineHeight={21} color={colors.Text_1}>
                        아쉬웠어요
                    </RegularText>
                    <MediumText fontSize={15} lineHeight={21} color={'#FF7173'}>
                        {Math.round(negativeRate)}%
                    </MediumText>
                </div>
            </div>
        </div>
    );
}

export default FeedInstructorReviewsCenterSummary;
